import { Options, Vue } from "vue-class-component";
import { BvTableFieldArray } from "bootstrap-vue/src/components/table";
import { AxiosResponse } from "axios";
import { BvTableCtxObject } from "bootstrap-vue";
import { useToast } from "vue-toastification";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";

export interface IPagedResultModel<TModel> {
  totalCount?: number;
  items?: Array<TModel> | null;
}

export interface IHasId {
  id?: string;
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
@Options({
  components: {}
})
abstract class BaseGrid<TModel extends IHasId, TPagedResultModel extends IPagedResultModel<TModel>> extends Vue {
  toast = useToast();
  router = useRouter();
  protected datas = {
    items: [] as TModel[],
    selected: [] as TModel[],
    total: 0
  };

  protected pagination = {
    maxResultCount: 10,
    page: 1
  };

  protected sort: {
    sortBy: string;
    sortDesc: false;
  } = {
    sortBy: "name",
    sortDesc: false
  };

  protected abstract meta: {
    formRoute: string;
  };

  protected isBusy = false;

  protected abstract fields: BvTableFieldArray;

  abstract getRequest(
    skipCount?: number,
    maxResultCount?: number,
    sorting?: string
  ): Promise<AxiosResponse<TPagedResultModel>>;

  abstract deleteRequest(id: string): Promise<AxiosResponse<void>>;

  async readData(currentPage?: number, sortString?: string) {
    this.isBusy = true;

    const sort = sortString ?? `${this.sort.sortBy} ${this.sort.sortDesc ? "desc" : "asc"}`;
    const skip = ((currentPage ?? this.pagination.page) - 1) * this.pagination.maxResultCount;

    await this.getRequest(skip, this.pagination.maxResultCount, sort)
      .then((response: AxiosResponse<TPagedResultModel>) => {
        this.datas.items = response.data.items as TModel[];
        this.datas.total = response.data.totalCount as number;
      })
      .catch(() => {
        this.toast.error("Error Reading Data", {
          toastClassName: "custom-error-toast",
          timeout: 5000
        });
      })
      .finally(() => {
        this.isBusy = false;
      });
  }

  mounted() {
    void this.readData();
  }

  async onPaginationChange(currentPage: number) {
    await this.readData(currentPage);
  }

  async onAddNew() {
    await this.router.push({
      name: this.meta.formRoute
    });
  }

  onRowSelected(items: TModel[]) {
    this.datas.selected = items;
  }

  async onSortChanged(ctx: BvTableCtxObject) {
    if (ctx.sortBy === "") {
      return;
    }
    const sort = `${ctx.sortBy} ${ctx.sortDesc ? "desc" : "asc"}`;
    await this.readData(undefined, sort);
  }

  async onDelete() {
    const selectedItemString = JSON.stringify(this.datas.selected);
    const selecteditem = JSON.parse(selectedItemString);
    await Swal.fire({
      title: "Please Confirm",
      text: "Please confirm that you want to delete everything.",
      width: 300,
      heightAuto: false,
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonColor: "#f05460",
      cancelButtonColor: "#595959",
      confirmButtonText: "OK",
      reverseButtons: true,
      showClass: {
        popup: "swal-slide-in"
      },
      hideClass: {
        popup: "swal-slide-out"
      },
      customClass: {
        popup: "swal-slide-top"
      },
      didOpen: () => {
        const confirmBtn = Swal.getConfirmButton();
        const cancelBtn = Swal.getCancelButton();
        const title = Swal.getTitle();
        const popup = Swal.getPopup();
        const text = popup?.querySelector(".swal2-html-container") as HTMLElement | null;
        const footer = popup?.querySelector(".swal2-actions") as HTMLElement | null;

        if (text) {
          text.style.fontSize = "16px";
          text.style.padding = "16px";
          text.style.textAlign = "left";
          text.style.overflow = "hidden";
        }

        if (popup) {
          popup.style.height = "200px";
        }

        if (confirmBtn) {
          confirmBtn.style.fontSize = "14px";
          confirmBtn.style.padding = "4px 8px";
          confirmBtn.style.height = "32px";
          confirmBtn.style.lineHeight = "1.2";
          confirmBtn.style.minWidth = "38px";
        }

        if (cancelBtn) {
          cancelBtn.style.fontSize = "14px";
          cancelBtn.style.padding = "4px 8px";
          cancelBtn.style.height = "32px";
          cancelBtn.style.lineHeight = "1.2";
          cancelBtn.style.minWidth = "60px";
        }

        if (title) {
          title.style.fontSize = "20px";
          title.style.padding = "16px";
          title.style.textAlign = "left";
        }

        if (footer) {
          footer.style.padding = "4px 8px";
          footer.style.gap = "1px";
          footer.style.minHeight = "auto";
          footer.style.lineHeight = "1";
          footer.style.justifyContent = "flex-end";
          footer.style.width = "100%";
        }
      }
    })
      .then(async result => {
        this.datas.selected = [];
        if (result.isConfirmed) {
          try {
            await this.deleteRequest(selecteditem.id as string);
            await this.readData();
          } catch (error: any) {
            console.log(error);
          }
        }
      })
      .catch((err: any) => {
        console.log(err);
      });
  }
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default class BaseGridMixin<TModel, TPagedResultModel> extends BaseGrid<TModel, TPagedResultModel> {}
